import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Gift, CheckCircle, XCircle, AlertTriangle, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Redeem = () => {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [result, setResult] = useState(null);
  const [claimed, setClaimed] = useState(false);

  const validatePrize = async () => {
    if (!input.trim()) return;
    
    setLoading(true);
    setResult(null);
    setClaimed(false);
    
    try {
      // Determine if input is code (6 digits) or phone number
      const isCode = /^\d{6}$/.test(input.trim());
      
      const response = await fetch(`${API_URL}/api/redeem/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isCode ? { code: input.trim() } : { phone: input.trim() })
      });
      
      const data = await response.json();
      setResult(data);
    } catch (error) {
      setResult({ valid: false, status: 'error', message: 'Connection error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const claimPrize = async () => {
    if (!result?.code) return;
    
    setClaiming(true);
    
    try {
      const response = await fetch(`${API_URL}/api/redeem/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: result.code, vendor_name: 'Staff' })
      });
      
      if (response.ok) {
        setClaimed(true);
        setResult({ ...result, valid: false, status: 'claimed', message: 'Prize successfully redeemed!' });
      } else {
        const error = await response.json();
        setResult({ ...result, valid: false, status: 'error', message: error.detail });
      }
    } catch (error) {
      setResult({ ...result, message: 'Failed to claim prize. Please try again.' });
    } finally {
      setClaiming(false);
    }
  };

  const getStatusDisplay = () => {
    if (!result) return null;
    
    if (claimed) {
      return (
        <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-6 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-green-700 mb-2">Prize Claimed!</h3>
          <p className="text-green-600">The prize has been marked as redeemed.</p>
        </div>
      );
    }
    
    if (result.valid) {
      return (
        <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-6">
          <div className="flex items-center justify-center mb-4">
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
          <h3 className="text-2xl font-bold text-green-700 text-center mb-4">Valid Prize!</h3>
          
          <div className="space-y-3 mb-6">
            <div className="bg-white rounded-xl p-4">
              <p className="text-sm text-gray-500">Winner</p>
              <p className="text-xl font-bold text-gray-900" data-testid="redeem-winner-name">{result.winner_name}</p>
            </div>
            <div className="bg-white rounded-xl p-4">
              <p className="text-sm text-gray-500">Prize</p>
              <p className="text-xl font-bold text-pink-600" data-testid="redeem-prize-name">{result.prize}</p>
            </div>
            <div className="bg-white rounded-xl p-4">
              <p className="text-sm text-gray-500">Won On</p>
              <p className="text-lg text-gray-700">{new Date(result.won_at).toLocaleDateString()}</p>
            </div>
          </div>
          
          <Button 
            onClick={claimPrize}
            disabled={claiming}
            className="w-full bg-green-500 hover:bg-green-600 text-white py-6 text-lg font-bold rounded-xl"
            data-testid="redeem-claim-btn"
          >
            {claiming ? 'Processing...' : '🎉 Mark as Redeemed'}
          </Button>
        </div>
      );
    }
    
    // Invalid states
    const statusConfig = {
      already_redeemed: {
        icon: <AlertTriangle className="w-12 h-12 text-yellow-500" />,
        bg: 'bg-yellow-50 border-yellow-200',
        title: 'Already Claimed',
        titleColor: 'text-yellow-700',
        message: result.redeemed_at ? `This prize was already claimed on ${new Date(result.redeemed_at).toLocaleDateString()}` : 'This prize has already been claimed.'
      },
      not_found: {
        icon: <XCircle className="w-12 h-12 text-red-500" />,
        bg: 'bg-red-50 border-red-200',
        title: 'Not Found',
        titleColor: 'text-red-700',
        message: 'No prize found for this code or phone number.'
      },
      error: {
        icon: <XCircle className="w-12 h-12 text-red-500" />,
        bg: 'bg-red-50 border-red-200',
        title: 'Error',
        titleColor: 'text-red-700',
        message: result.message
      }
    };
    
    const config = statusConfig[result.status] || statusConfig.error;
    
    return (
      <div className={`${config.bg} border-2 rounded-2xl p-6 text-center`}>
        <div className="flex justify-center mb-4">{config.icon}</div>
        <h3 className={`text-2xl font-bold ${config.titleColor} mb-2`}>{config.title}</h3>
        <p className="text-gray-600">{config.message}</p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
      <Helmet>
        <title>Prize Redemption | 1st Ave ICP</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      
      <div className="max-w-md mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Gift className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Prize Redemption</h1>
          <p className="text-gray-500 mt-2">Validate winner prizes</p>
        </div>
        
        {/* Input */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Enter Code or Phone Number
          </label>
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="847291 or (817) 555-1234"
              className="flex-1 text-lg py-6"
              onKeyDown={(e) => e.key === 'Enter' && validatePrize()}
              data-testid="redeem-input"
            />
            <Button 
              onClick={validatePrize}
              disabled={loading || !input.trim()}
              className="bg-pink-500 hover:bg-pink-600 px-6"
              data-testid="redeem-validate-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Search className="w-5 h-5" />
              )}
            </Button>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Enter the 6-digit redemption code or customer's phone number
          </p>
        </div>
        
        {/* Result */}
        {result && getStatusDisplay()}
        
        {/* Clear Button */}
        {result && (
          <Button
            variant="ghost"
            onClick={() => { setResult(null); setInput(''); setClaimed(false); }}
            className="w-full mt-4 text-gray-500"
          >
            Check Another Prize
          </Button>
        )}
        
        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-8">
          1st Ave Ice Cream & Pastry<br />
          Staff Use Only
        </p>
      </div>
    </div>
  );
};

export default Redeem;
